import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';

interface Medecin {
  id: string;
  nom: string;
  prenom: string;
  specialite: string;
}

interface Disponibilite {
  id: string;
  medecin_id: string;
  jour_semaine: number;
  heure_debut: string;
  heure_fin: string;
  actif: boolean;
}

interface RendezVous {
  id: string;
  patient_id: string;
  medecin_id: string;
  date_rdv: string;
  heure_debut: string;
  heure_fin: string;
  motif: string | null;
  statut: string;
  patients?: {
    nom: string;
    prenom: string;
  } | null;
  medecins?: {
    nom: string;
    prenom: string;
    specialite: string;
  } | null;
}

interface ModifierRendezVousModalProps {
  isOpen: boolean;
  rendezVous: RendezVous | null;
  onClose: () => void;
  onSuccess: () => void;
}

const JOURS_SEMAINE = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];

export default function ModifierRendezVousModal({ isOpen, rendezVous, onClose, onSuccess }: ModifierRendezVousModalProps) {
  const [medecins, setMedecins] = useState<Medecin[]>([]);
  const [disponibilites, setDisponibilites] = useState<Disponibilite[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedMedecin, setSelectedMedecin] = useState('');
  const [dateRdv, setDateRdv] = useState('');
  const [heureDebut, setHeureDebut] = useState('');
  const [heureFin, setHeureFin] = useState('');
  const [motif, setMotif] = useState('');

  useEffect(() => {
    if (isOpen && rendezVous) {
      setSelectedMedecin(rendezVous.medecin_id);
      setDateRdv(rendezVous.date_rdv);
      setHeureDebut(rendezVous.heure_debut ? rendezVous.heure_debut.substring(0, 5) : '');
      setHeureFin(rendezVous.heure_fin ? rendezVous.heure_fin.substring(0, 5) : '');
      setMotif(rendezVous.motif || '');
    }
  }, [isOpen, rendezVous]);

  useEffect(() => {
    loadMedecins();
  }, []);

  useEffect(() => {
    if (selectedMedecin) {
      loadDisponibilites(selectedMedecin);
    }
  }, [selectedMedecin]);

  const loadMedecins = async () => {
    const { data } = await supabase
      .from('medecins')
      .select('id, nom, prenom, specialite')
      .order('nom');
    if (data) setMedecins(data);
  };

  const loadDisponibilites = async (medecinId: string) => {
    const { data } = await supabase
      .from('disponibilites_medecins')
      .select('*')
      .eq('medecin_id', medecinId)
      .eq('actif', true)
      .order('jour_semaine');
    if (data) setDisponibilites(data);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!rendezVous) return;

    setLoading(true);
    try {
      // Vérifier les conflits de créneau
      const { data: overlappingRdv, error: overlapError } = await supabase
        .from('rendez_vous')
        .select('id, heure_debut, heure_fin')
        .eq('medecin_id', selectedMedecin)
        .eq('date_rdv', dateRdv)
        .neq('id', rendezVous.id)
        .neq('statut', 'annule');

      if (overlapError) throw overlapError;

      if (overlappingRdv && overlappingRdv.length > 0) {
        const hasOverlap = overlappingRdv.some((rdv: any) => {
          return (
            (heureDebut >= rdv.heure_debut && heureDebut < rdv.heure_fin) ||
            (heureFin > rdv.heure_debut && heureFin <= rdv.heure_fin) ||
            (heureDebut <= rdv.heure_debut && heureFin >= rdv.heure_fin)
          );
        });

        if (hasOverlap) {
          alert('Ce créneau horaire chevauche un rendez-vous existant. Veuillez choisir une autre heure.');
          setLoading(false);
          return;
        }
      }

      const { error } = await supabase
        .from('rendez_vous')
        .update({
          medecin_id: selectedMedecin,
          date_rdv: dateRdv,
          heure_debut: heureDebut,
          heure_fin: heureFin,
          motif: motif || null,
          date_modification: new Date().toISOString()
        })
        .eq('id', rendezVous.id);

      if (error) {
        if (error.code === '23505') {
          alert('Ce créneau horaire est déjà réservé pour ce médecin.');
          return;
        }
        throw error;
      }

      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Erreur modification RDV:', error);
      alert(error.message || 'Erreur lors de la modification du rendez-vous');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !rendezVous) return null;

  const patientName = rendezVous.patients
    ? `${rendezVous.patients.nom} ${rendezVous.patients.prenom}`
    : 'Patient';

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Modifier le rendez-vous</h2>
              <p className="text-sm text-gray-600 mt-1">Patient : {patientName}</p>
            </div>
            <button
              onClick={onClose}
              className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 transition-all cursor-pointer"
            >
              <i className="ri-close-line text-xl text-gray-600"></i>
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Médecin <span className="text-red-500">*</span>
              </label>
              <select
                required
                value={selectedMedecin}
                onChange={(e) => setSelectedMedecin(e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-clinic-blue text-sm cursor-pointer"
              >
                <option value="">Sélectionner un médecin</option>
                {medecins.map((m) => (
                  <option key={m.id} value={m.id}>
                    Dr. {m.nom} {m.prenom} - {m.specialite}
                  </option>
                ))}
              </select>
            </div>

            {disponibilites.length > 0 && (
              <div className="p-4 bg-blue-50 border-2 border-blue-200 rounded-2xl">
                <h3 className="text-sm font-bold text-blue-900 mb-3">Disponibilités du médecin</h3>
                <div className="grid grid-cols-2 gap-2">
                  {disponibilites.map((d) => (
                    <div key={d.id} className="flex items-center gap-2 p-2 bg-white rounded-lg text-xs">
                      <i className="ri-calendar-check-line text-blue-600"></i>
                      <div>
                        <span className="font-semibold text-gray-900">{JOURS_SEMAINE[d.jour_semaine]}</span>
                        <span className="text-gray-600 ml-1">{d.heure_debut?.substring(0, 5)} - {d.heure_fin?.substring(0, 5)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                required
                min={new Date().toISOString().split('T')[0]}
                value={dateRdv}
                onChange={(e) => setDateRdv(e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-clinic-blue text-sm"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Heure début <span className="text-red-500">*</span>
                </label>
                <input
                  type="time"
                  required
                  value={heureDebut}
                  onChange={(e) => setHeureDebut(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-clinic-blue text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Heure fin <span className="text-red-500">*</span>
                </label>
                <input
                  type="time"
                  required
                  value={heureFin}
                  onChange={(e) => setHeureFin(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-clinic-blue text-sm"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Motif</label>
              <textarea
                value={motif}
                onChange={(e) => setMotif(e.target.value)}
                rows={3}
                maxLength={500}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-clinic-blue text-sm resize-none"
                placeholder="Motif de consultation..."
              />
            </div>

            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-6 py-4 border-2 border-gray-300 text-gray-700 rounded-full hover:bg-gray-50 transition-all font-semibold whitespace-nowrap cursor-pointer"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 px-6 py-4 bg-clinic-blue text-white rounded-full hover:bg-clinic-blue/90 transition-all font-semibold shadow-lg disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {loading ? (
                  <>
                    <i className="ri-loader-4-line animate-spin mr-2"></i>
                    Modification...
                  </>
                ) : (
                  <>
                    <i className="ri-save-line mr-2"></i>
                    Enregistrer
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}